import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';
import { TimeEventService } from '@intranet/modules/timer/time-event.service';

/** Payload emitted when we detect that the app died with a timer running.
 *  Carries everything the recovery modal needs to render and act. */
export interface CrashRecoveryEvent {
    /** Best estimate of when the app died — the last heartbeat we wrote. */
    crashAt: Date;
    /** How long the app was dead for (now - crashAt). */
    gapMs: number;
}

interface HeartbeatRecord {
    lastAlive: number; // epoch ms
}

/**
 * Detects when the app died with a timer running, so the next session can
 * prompt the user to clean up the resulting "phantom" time.
 *
 * How it works:
 *
 *   1. While a timer is running, we write `{ lastAlive: <now> }` to
 *      localStorage every 60s. This is the heartbeat.
 *   2. On normal timer stop, the heartbeat is cleared.
 *   3. On *unclean* shutdown (crash, power off, force-quit, OS reboot),
 *      the heartbeat is left behind with a stale timestamp.
 *   4. Next app start, we read the heartbeat. If it's stale by more than
 *      the threshold *and* the server still has a running timer that
 *      started before the stale moment, we emit a recovery event. The
 *      `CrashRecoveryModalComponent` picks it up and prompts the user.
 *
 * Same actions as idle-resolution-modal: keep the time, push the start
 * forward (discard the gap), or stop the timer at the crash moment.
 *
 * Why this and not just "running for X hours" heuristic: a long-running
 * timer is sometimes legitimate (overnight work, batch processes). A
 * heartbeat gives an exact gap so we only prompt when the app genuinely
 * wasn't running, not when the user just kept the timer going.
 */
@Injectable({ providedIn: 'root' })
export class CrashRecoveryService implements OnDestroy {
    /** localStorage key. Versioned in case the shape changes later. */
    private readonly STORAGE_KEY = 'visarc-timer-heartbeat-v1';
    /** Write a fresh timestamp every minute while a timer is running.
     *  Frequent enough that the worst-case gap detection is ~60s off. */
    private readonly HEARTBEAT_INTERVAL_MS = 10_000; // TESTING ONLY — production: 60_000
    /** Below this, the gap is treated as a normal reload — a build-version
     *  refresh, a Ctrl+R, the brief window between window-close and
     *  window-open. We don't want to prompt for those. */
    private readonly GAP_THRESHOLD_MS = 10_000; // TESTING ONLY — production: 2 * 60_000
    /** Above this, the heartbeat is suspect — clock drift, manual edit of
     *  localStorage, etc. Discard rather than show a bewildering prompt. */
    private readonly MAX_REASONABLE_GAP_MS = 30 * 24 * 60 * 60_000; // 30 days
    /** How long to wait for the running-timer state to load from the
     *  server after a stale heartbeat is detected. If no running timer
     *  surfaces in this window, the recovery is moot (someone stopped
     *  the timer elsewhere) and we clean up silently. */
    private readonly STARTUP_WAIT_MS = 10_000; // TESTING ONLY — production: 30_000

    private readonly _recoveryDetected$ = new Subject<CrashRecoveryEvent>();
    readonly recoveryDetected$: Observable<CrashRecoveryEvent> =
        this._recoveryDetected$.asObservable();

    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private startupTimeout: ReturnType<typeof setTimeout> | null = null;
    /** Set if a stale heartbeat was detected at startup; emitted on the
     *  first `newEntry$` that surfaces a server-side running timer whose
     *  start is before the crash time. Cleared after emit or after the
     *  startup wait window expires. */
    private pendingRecovery: CrashRecoveryEvent | null = null;

    private newEntrySub: Subscription | null = null;
    private stopEntrySub: Subscription | null = null;

    /** Grace window when comparing the server's "ms since last ping" to
     *  the local stale-heartbeat gap. Pings from two clients are not
     *  perfectly aligned (each on its own interval), so we allow some
     *  slop — at least the heartbeat interval plus a small buffer.
     *  Below this, the server's "last seen" is treated as effectively
     *  the same as our own last ping (i.e. no other client was active). */
    private readonly OTHER_CLIENT_GRACE_MS = this.HEARTBEAT_INTERVAL_MS + 30_000;

    constructor(
        private timerEntry: TimerEntryService,
        private timeEvent: TimeEventService,
    ) {
        this.checkOnStartup();
        this.wireTimerStateSubscriptions();
        // Defensive: if the timer was already loaded by the time we
        // constructed (rare, depends on injection order), start the
        // heartbeat now. Also resolve any pending recovery against the
        // current state straight away rather than waiting for the next
        // newEntry$ — which may never come if data is already loaded.
        if (this.timerEntry.isLiveTimerRunning) {
            this.startHeartbeat();
            this.resolvePendingRecovery();
        }
    }

    // ─── Startup check ──────────────────────────────────────────────────

    private checkOnStartup(): void {
        const heartbeat = this.readHeartbeat();
        if (!heartbeat) {
            console.log('[crash-recovery] startup: no heartbeat in localStorage');
            return;
        }
        const gap = Date.now() - heartbeat.lastAlive;
        console.log(`[crash-recovery] startup: heartbeat was ${Math.round(gap / 1000)}s ago`);
        // Clock went backwards (system clock adjustment, NTP correction)
        // or heartbeat is bizarrely old. Either way, discard.
        if (gap <= 0 || gap > this.MAX_REASONABLE_GAP_MS) {
            console.log('[crash-recovery] startup: gap out of reasonable range, discarding');
            this.clearHeartbeat();
            return;
        }
        // Below threshold = trivial gap (quick reload). Leave the
        // heartbeat alone — the next interval tick will overwrite it
        // with a fresh value if a timer is running.
        if (gap < this.GAP_THRESHOLD_MS) {
            console.log(`[crash-recovery] startup: gap below ${this.GAP_THRESHOLD_MS / 1000}s threshold, ignoring`);
            return;
        }
        console.log('[crash-recovery] startup: stale heartbeat detected, waiting for running-timer state to load');
        this.pendingRecovery = {
            crashAt: new Date(heartbeat.lastAlive),
            gapMs: gap,
        };
        // If no running timer ever surfaces (e.g. someone stopped it from
        // a browser tab in between sessions), give up after STARTUP_WAIT_MS
        // and clean up — the prompt would be meaningless without a timer
        // to act on.
        this.startupTimeout = setTimeout(() => {
            if (this.pendingRecovery) {
                this.pendingRecovery = null;
                this.clearHeartbeat();
            }
            this.startupTimeout = null;
        }, this.STARTUP_WAIT_MS);
    }

    /** Try to fulfil a pending recovery against the current timer state.
     *  Called from both `newEntry$` (timer just loaded from server) and
     *  the constructor (defensive, in case the timer is already loaded). */
    private resolvePendingRecovery(): void {
        if (!this.pendingRecovery) return;
        if (!this.timerEntry.isLiveTimerRunning) {
            console.log('[crash-recovery] resolve: pending, but no running timer yet');
            return;
        }
        const liveStart = this.timerEntry.liveStartTime;
        if (!liveStart) {
            console.log('[crash-recovery] resolve: pending, but no liveStartTime (unexpected)');
            return;
        }
        // If the running timer started AFTER the crash, it isn't the one
        // that was running when we died — someone manually started a
        // fresh one in between. The crash recovery is no longer relevant
        // to the user's current work; clear and move on.
        if (liveStart.getTime() >= this.pendingRecovery.crashAt.getTime()) {
            console.log(
                `[crash-recovery] resolve: timer started at ${liveStart.toISOString()} which is AFTER the crash at ${this.pendingRecovery.crashAt.toISOString()} — skipping prompt`,
            );
            this.pendingRecovery = null;
            this.clearStartupTimeout();
            this.clearHeartbeat();
            return;
        }
        // Server presence check: ask the server when it last saw any
        // client ping for this user. If the most recent ping is much
        // more recent than our local stale-heartbeat gap, another client
        // (e.g. browser tab if we're the desktop app, or vice versa)
        // kept tracking time while we were down — the "gap" wasn't a
        // real gap, so the recovery prompt would just confuse the user.
        // Suppress it silently. Fall through to the existing prompt
        // path if the server has no record, no other client was active,
        // or the call fails (network, server, etc.).
        const recovery = this.pendingRecovery;
        this.timeEvent.getRunningTimer().subscribe({
            next: res => {
                const otherClientActive =
                    typeof res.lastSeenMsAgo === 'number'
                    && res.lastSeenMsAgo + this.OTHER_CLIENT_GRACE_MS < recovery.gapMs;
                if (otherClientActive) {
                    console.log(
                        `[crash-recovery] resolve: another client active (server last seen ${Math.round((res.lastSeenMsAgo as number) / 1000)}s ago vs local gap ${Math.round(recovery.gapMs / 1000)}s) — suppressing prompt`,
                    );
                    this.pendingRecovery = null;
                    this.clearStartupTimeout();
                    this.clearHeartbeat();
                    return;
                }
                this.emitRecovery(recovery, liveStart);
            },
            error: err => {
                // Server unreachable — fall back to the existing prompt
                // path. Better to over-prompt than to silently drop a
                // legitimate recovery because the network was flaky.
                console.error('[crash-recovery] presence check failed, prompting anyway:', err);
                this.emitRecovery(recovery, liveStart);
            },
        });
    }

    /** Final stage of the resolution pipeline — actually fire the
     *  recovery event. Split out so both the success and failure paths
     *  of the presence check can call it without duplication. */
    private emitRecovery(recovery: CrashRecoveryEvent, liveStart: Date): void {
        if (this.pendingRecovery !== recovery) return; // already resolved by another path
        console.log(
            `[crash-recovery] resolve: emitting recovery — gap ${Math.round(recovery.gapMs / 1000)}s, timer start ${liveStart.toISOString()}, crash at ${recovery.crashAt.toISOString()}`,
        );
        this._recoveryDetected$.next(recovery);
        this.pendingRecovery = null;
        this.clearStartupTimeout();
    }

    // ─── Timer state wiring ────────────────────────────────────────────

    private wireTimerStateSubscriptions(): void {
        this.newEntrySub = this.timerEntry.newEntry$.subscribe(() => {
            this.startHeartbeat();
            // newEntry$ fires both for "loaded existing running timer from
            // server" and "user just started a new timer". Either way, try
            // to resolve any pending recovery — the time-comparison inside
            // resolvePendingRecovery handles the two cases correctly.
            this.resolvePendingRecovery();
        });
        this.stopEntrySub = this.timerEntry.stopEntry$.subscribe(() => {
            this.stopHeartbeat();
            this.clearHeartbeat();
        });
    }

    // ─── Heartbeat ──────────────────────────────────────────────────────

    private startHeartbeat(): void {
        if (this.heartbeatTimer) return; // already running
        this.writeHeartbeat();
        this.heartbeatTimer = setInterval(
            () => this.writeHeartbeat(),
            this.HEARTBEAT_INTERVAL_MS,
        );
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private writeHeartbeat(): void {
        try {
            const record: HeartbeatRecord = { lastAlive: Date.now() };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(record));
        } catch (e) {
            // localStorage can throw (quota, private mode, security policy).
            // Crash recovery degrades to a no-op in that case — the app
            // continues to work normally.
            console.error('[crash-recovery] failed to write heartbeat:', e);
        }
        // Fire-and-forget server presence ping so another client (browser
        // tab vs. desktop app) can tell — at its own startup — that we
        // were still alive after its localStorage heartbeat went stale.
        // Errors are swallowed: a missed ping just means slightly stale
        // presence data, not a broken timer.
        this.timeEvent.heartbeat().subscribe({
            error: err => console.error('[crash-recovery] server heartbeat failed:', err),
        });
    }

    private readHeartbeat(): HeartbeatRecord | null {
        try {
            const raw = localStorage.getItem(this.STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as Partial<HeartbeatRecord>;
            if (typeof parsed?.lastAlive !== 'number') return null;
            return { lastAlive: parsed.lastAlive };
        } catch {
            // Corrupt JSON or storage unavailable — treat as missing.
            return null;
        }
    }

    private clearHeartbeat(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch {
            // Ignore — see writeHeartbeat for context.
        }
    }

    private clearStartupTimeout(): void {
        if (this.startupTimeout) {
            clearTimeout(this.startupTimeout);
            this.startupTimeout = null;
        }
    }

    ngOnDestroy(): void {
        this.stopHeartbeat();
        this.clearStartupTimeout();
        this.newEntrySub?.unsubscribe();
        this.stopEntrySub?.unsubscribe();
    }
}
